/**
 * Realtime run event protocol (prompt.md §48, §49, §76, §77).
 *
 * Model output streaming is a *view* of durable run state, never the only
 * record. Every stream carries a monotonic sequence number so a reconnecting
 * client can ask for "everything after 174" and get exactly what it missed.
 */

import type { ApprovalId, EventId, FileId, MessageId, RunId, RunState, ToolCallId } from "@dayvise/domain";

export const RUN_EVENT_SCHEMA_VERSION = 1;

export const RUN_EVENT_TYPES = [
  "run.created",
  "run.queued",
  "run.started",
  "assistant.output.delta",
  "assistant.message.completed",
  "tool.requested",
  "tool.started",
  "tool.output",
  "tool.completed",
  "file.created",
  "file.updated",
  "run.waiting_approval",
  "approval.received",
  "run.waiting_clarification",
  "clarification.received",
  "run.steered",
  "run.paused",
  "run.resumed",
  "run.usage",
  "run.error",
  "run.compacted",
  "run.todo_updated",
  "file.patched",
  "process.output",
  "process.prompt",
  "run.completed",
] as const;

export type RunEventType = (typeof RUN_EVENT_TYPES)[number];

export interface RunEventPayloadMap {
  "run.created": { runId: RunId; conversationId: string; branchId: string; trigger: string };
  "run.queued": { runId: RunId; position: number };
  "run.started": { runId: RunId; attempt: number };
  "assistant.output.delta": { runId: RunId; delta: string; messageId: MessageId | null };
  "assistant.message.completed": { runId: RunId; messageId: MessageId; text: string };
  "tool.requested": { runId: RunId; toolCallId: ToolCallId; toolId: string; arguments: unknown };
  "tool.started": { runId: RunId; toolCallId: ToolCallId; toolId: string };
  "tool.output": { runId: RunId; toolCallId: ToolCallId; chunk: string };
  "tool.completed": { runId: RunId; toolCallId: ToolCallId; ok: boolean; result: unknown };
  "file.created": { runId: RunId; fileId: FileId; path: string; size: number };
  "file.updated": { runId: RunId; fileId: FileId; path: string; version: number };
  "run.waiting_approval": { runId: RunId; approvalId: ApprovalId; toolId: string; arguments: unknown; riskLevel: string };
  "approval.received": { runId: RunId; approvalId: ApprovalId; decision: "approved" | "rejected" };
  "run.waiting_clarification": { runId: RunId; clarificationId: string; questions: unknown };
  "clarification.received": { runId: RunId; clarificationId: string; answers: unknown };
  "run.steered": { runId: RunId; message: string; controlSequence: number };
  "run.paused": { runId: RunId; reason: string; state?: RunState; checkpointId: string | null };
  "run.resumed": { runId: RunId; checkpointId: string | null };
  "run.usage": { runId: RunId; inputTokens: number; outputTokens: number; toolCalls: number };
  "run.error": { runId: RunId; errorClass: string; message: string; retryable: boolean };
  "run.compacted": { runId: RunId; tokensBefore: number; tokensAfter: number; messagesCompacted: number; summary: string };
  "run.todo_updated": {
    runId: RunId;
    todos: readonly {
      readonly id: string;
      readonly content: string;
      readonly status: "pending" | "in_progress" | "completed" | "cancelled";
      readonly parent?: string;
    }[];
    revision: number;
    summary: {
      readonly total: number;
      readonly pending: number;
      readonly in_progress: number;
      readonly completed: number;
      readonly cancelled: number;
    };
  };
  "file.patched": {
    runId: RunId;
    path?: string;
    filesModified: readonly string[];
    filesCreated: readonly string[];
    filesDeleted: readonly string[];
    diff: string;
  };
  "process.output": {
    runId: RunId;
    processId: string;
    output: string;
  };
  "process.prompt": {
    runId: RunId;
    processId: string;
    prompt: string;
  };
  "run.completed": { runId: RunId; state: string; finishReason: string | null };
}

export interface RunStreamEvent<T extends RunEventType = RunEventType> {
  readonly eventId: EventId;
  readonly runId: RunId;
  /** Monotonic within a run's stream. Clients resume from the last value seen. */
  readonly sequence: number;
  readonly timestamp: string;
  readonly type: T;
  readonly schemaVersion: number;
  readonly payload: RunEventPayloadMap[T];
}

export function makeRunEvent<T extends RunEventType>(
  type: T,
  runId: RunId,
  sequence: number,
  payload: RunEventPayloadMap[T],
  eventId: EventId,
  timestamp = new Date().toISOString(),
): RunStreamEvent<T> {
  return { eventId, runId, sequence, timestamp, type, schemaVersion: RUN_EVENT_SCHEMA_VERSION, payload };
}

export interface ResumeRequest {
  readonly runId: RunId;
  /** Last sequence the client successfully processed. */
  readonly afterSequence: number;
}

export interface ResumeResponse {
  readonly events: readonly RunStreamEvent[];
  /** True when the requested window is no longer buffered and a snapshot is needed. */
  readonly snapshotRequired: boolean;
  readonly latestSequence: number;
}

/** How many events a run stream keeps for replay before requiring a snapshot. */
export const RUN_STREAM_BUFFER_SIZE = 1000;

export const CLIENT_PROTOCOL_VERSION = "v1";

/** Capability manifest advertised by a connecting client (prompt.md §45). */
export interface ClientCapabilityManifest {
  readonly platform: "web" | "desktop" | "android";
  readonly capabilities: readonly string[];
  readonly appVersion?: string;
  readonly protocolVersion: string;
}

/**
 * A capability-declaration guard stood here: `isCapabilityDeclarationOnly()`, which took no arguments
 * and returned `true` unconditionally while its docstring stated a rule — a capability declaration is
 * never authorization. It cannot enforce, or even discriminate, what it appeared to assert, and
 * nothing called it.
 *
 * The rule still holds, and it holds by absence: no code path reads a client's declaration, so none
 * can be granted from one. `ClientCapabilityManifest` above describes the §45 shape for a handshake
 * that does not exist yet and is consumed by nothing today. Recording that is more useful than a
 * guard-shaped function that always agrees.
 */
export type RealtimeMessage =
  | { readonly kind: "hello"; readonly serverTime: string; readonly protocolVersion: string }
  | { readonly kind: "subscribe"; readonly runId: RunId; readonly afterSequence: number }
  | { readonly kind: "unsubscribe"; readonly runId: RunId }
  | { readonly kind: "event"; readonly event: RunStreamEvent }
  | { readonly kind: "resume"; readonly response: ResumeResponse }
  | { readonly kind: "error"; readonly code: string; readonly message: string }
  | { readonly kind: "ping"; readonly nonce: number }
  | { readonly kind: "pong"; readonly nonce: number };
